import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Button,
  Divider,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
} from '@mui/material';
import {
  ArrowBack,
  People,
  CheckCircle,
  Cancel,
  Assessment,
  TrendingUp,
  TrendingDown,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { examinationAPI } from '../../../services/api';

const ExaminationStats = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [examination, setExamination] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      setLoading(true);

      const [examResponse, statsResponse] = await Promise.all([
        examinationAPI.getOne(id),
        examinationAPI.getStats(id),
      ]);

      const examData = examResponse.data.data?.examination || examResponse.data.data || examResponse.data;
      setExamination(examData);

      setStats(statsResponse.data.data || statsResponse.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load examination statistics');
    } finally {
      setLoading(false);
    }
  };

  const calculateTotals = () => {
    if (!examination?.subjects) return { totalMarks: 0, passingMarks: 0 };
    return {
      totalMarks: examination.subjects.reduce((sum, s) => sum + (s.totalMarks || 0), 0),
      passingMarks: examination.subjects.reduce((sum, s) => sum + (s.passingMarks || 0), 0),
    };
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!examination) {
    return (
      <Box>
        <Typography variant="h6">Examination not found</Typography>
        <Button onClick={() => navigate('/admin/examinations')} sx={{ mt: 2 }}>
          Back to Examinations
        </Button>
      </Box>
    );
  }

  const totals = calculateTotals();

  const totalStudents = stats?.totalRegistered || 0;
  const appeared = stats?.appeared || 0;
  const passed = stats?.passed || 0;
  const failed = appeared - passed;
  const absent = totalStudents - appeared;
  const passPercentage = Number(stats?.passPercentage || 0);
  const averageMarks = Number(stats?.averageMarks || 0);

  const mockStats = {
    totalStudents,
    appeared,
    absent: absent < 0 ? 0 : absent,
    passed,
    failed: failed < 0 ? 0 : failed,
    averageMarks,
    passPercentage,
    subjectStats: stats?.subjectStats || examination.subjects?.map(subject => ({
      subjectId: subject.subjectId || subject._id,
      name: subject.name,
      code: subject.code,
      totalMarks: subject.totalMarks || 0,
      averageMarks: 0,
      highestMarks: 0,
      lowestMarks: 0,
      passPercentage: 0,
    })) || [],
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBack />}
            onClick={() => navigate(`/admin/examinations/${id}`)}
          >
            Back
          </Button>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              {examination.name} - Statistics
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Examination performance and analytics
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Overview Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={2}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Total Students
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {mockStats.totalStudents}
                  </Typography>
                </Box>
                <People sx={{ fontSize: 48, color: 'primary.main', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={2}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Appeared
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {mockStats.appeared}
                  </Typography>
                </Box>
                <CheckCircle sx={{ fontSize: 48, color: 'success.main', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={2}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Passed
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: 'success.main' }}>
                    {mockStats.passed}
                  </Typography>
                </Box>
                <TrendingUp sx={{ fontSize: 48, color: 'success.main', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={2}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Failed
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: 'error.main' }}>
                    {mockStats.failed}
                  </Typography>
                </Box>
                <TrendingDown sx={{ fontSize: 48, color: 'error.main', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Performance Metrics */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper elevation={2} sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Overall Performance
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">Pass Percentage</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {mockStats.passPercentage.toFixed(1)}%
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={mockStats.passPercentage}
                sx={{ height: 8, borderRadius: 4 }}
                color={mockStats.passPercentage >= 75 ? 'success' : mockStats.passPercentage >= 50 ? 'warning' : 'error'}
              />
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Average Marks
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                {mockStats.averageMarks.toFixed(2)} / {totals.totalMarks}
              </Typography>
            </Box>

            <Box>
              <Typography variant="body2" color="text.secondary">
                Passing Threshold
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                {totals.passingMarks} / {totals.totalMarks}
              </Typography>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper elevation={2} sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Attendance
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Box sx={{ display: 'flex', justifyContent: 'space-around', mt: 3 }}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h3" sx={{ fontWeight: 700, color: 'success.main' }}>
                  {mockStats.appeared}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Appeared
                </Typography>
              </Box>
              <Divider orientation="vertical" flexItem />
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h3" sx={{ fontWeight: 700, color: 'error.main' }}>
                  {mockStats.absent}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Absent
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>

        {/* Subject-wise Performance */}
        <Grid item xs={12}>
          <Paper elevation={2} sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Subject-wise Performance
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Subject</strong></TableCell>
                    <TableCell align="center"><strong>Max Marks</strong></TableCell>
                    <TableCell align="center"><strong>Average</strong></TableCell>
                    <TableCell align="center"><strong>Highest</strong></TableCell>
                    <TableCell align="center"><strong>Lowest</strong></TableCell>
                    <TableCell align="center"><strong>Pass %</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {mockStats.subjectStats.map((subject, idx) => (
                    <TableRow key={subject.subjectId || idx}>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {subject.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {subject.code}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="center">{subject.totalMarks}</TableCell>
                      <TableCell align="center">{subject.averageMarks.toFixed(1)}</TableCell>
                      <TableCell align="center">{subject.highestMarks}</TableCell>
                      <TableCell align="center">{subject.lowestMarks}</TableCell>
                      <TableCell align="center">
                        <Chip
                          label={`${subject.passPercentage.toFixed(1)}%`}
                          size="small"
                          color={subject.passPercentage >= 75 ? 'success' : subject.passPercentage >= 50 ? 'warning' : 'error'}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {mockStats.subjectStats.length === 0 && (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Assessment sx={{ fontSize: 64, color: 'text.secondary', opacity: 0.3 }} />
                <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
                  No statistics available yet. Results will appear once students complete the examination.
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ExaminationStats;
